import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

/**
 * Dedicated URL aliases for the relevant Legal page anchors.
 * Google AdSense and most ad networks check for these specific URLs;
 * a dedicated /privacy and /terms route satisfies that requirement even
 * though the canonical full text lives in /legal.
 *
 * Each alias renders proper meta tags + a brief crawlable snippet, then
 * client-side-redirects to the anchor inside /legal where the full text lives.
 */
const Alias = ({ title, description, canonical, anchor, snippet, heading }) => {
  const navigate = useNavigate();
  useEffect(() => {
    // Allow the crawler time to read the snippet, then bounce a human visitor to the full document.
    const timer = setTimeout(() => navigate(`/legal#${anchor}`, { replace: true }), 50);
    return () => clearTimeout(timer);
  }, [navigate, anchor]);

  return (
    <div className="min-h-screen bg-rooam-black text-white">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:url" content={canonical} />
        <meta property="og:title" content={title} />
      </Helmet>
      <main className="max-w-3xl mx-auto px-4 py-16">
        <h1 className="text-3xl font-bold mb-4">{heading}</h1>
        <p className="text-zinc-400">{snippet}</p>
        <p className="text-zinc-500 text-sm mt-6">
          Redirecting to the full document…
        </p>
      </main>
    </div>
  );
};

export const Privacy = () => (
  <Alias
    title="Privacy Policy — Rooam Sports Network"
    description="How Rooam Sports Network collects, uses, and protects personal information for athletes, parents, viewers, and tournament directors. Includes COPPA compliance, payment data handling, GDPR/CCPA rights, and your control over your data."
    canonical="https://rooamsports.network/privacy"
    anchor="privacy-policy"
    heading="Privacy Policy"
    snippet="Rooam Sports Network respects your privacy. This document explains exactly what data we collect, why we collect it, how long we keep it, and the controls you have over it. It also describes how we comply with COPPA for athletes under 13, CCPA for California residents, and GDPR for visitors from the European Economic Area."
  />
);

export const Terms = () => (
  <Alias
    title="Terms of Use — Rooam Sports Network"
    description="Official Terms of Use for Rooam Sports Network — covering account responsibilities, content licensing, prohibited conduct, dispute resolution, arbitration, and subscription policies."
    canonical="https://rooamsports.network/terms"
    anchor="terms-of-use"
    heading="Terms of Use"
    snippet="These Terms of Use govern your access to and use of Rooam Sports Network and all related services. By using the platform, you agree to these terms, including the licensing of user-uploaded content, prohibited conduct rules, our subscription and cancellation policies, and the dispute-resolution and arbitration provisions."
  />
);
